import filter from "lodash/filter";
import { IntlShape } from "react-intl";
import { authorizedUpsells } from "@Components/molecules/customization/Upsells/whiteLists";
import { CheerzApiCustomizationProductOption, SelectedUpsell } from "@Types/api/customization";
import { Currency, Price } from "@Types/api/price";
import { Upsell, UpsellIdentifiers, UpsellWhitelistedProductOption } from "@Types/upsells";

interface ProductOption {
  productOptionTag: string;
  optionValueTag: string;
  quantity: number;
}
type AdditionalOptions = ProductOption[];

export const getUpsellsToDisplay = (
  productOptions: AdditionalOptions,
  tag: string
): AdditionalOptions => {
  return filter(productOptions, (option) => option.productOptionTag !== "additional_photos").filter(
    (option) => !ignoredOptionTag(option.productOptionTag) && !ignoredProductTag(tag)
  );
};

export const getUpsellCompleteTag = (productOptionTag: string, optionValueTag: string): string => {
  let completeTag = productOptionTag;
  if (optionValueTag !== "default") {
    completeTag = `${completeTag}_${optionValueTag}`;
  }
  return completeTag;
};

export const getUpsellValues = (
  option: ProductOption,
  total: Price,
  intl: IntlShape
):
  | {
      giftAmount: string;
      printedCard: string;
    }
  | undefined => {
  if (option.productOptionTag === "amount") {
    return {
      giftAmount: intl.formatNumber(option.quantity, {
        currency: total.currency,
        style: "currency",
      }),
      printedCard: intl.formatNumber(total.cents / 100 - option.quantity, {
        currency: total.currency,
        style: "currency",
      }),
    };
  }
};

export const getDefaultByValueTag = (optionValueTag: string): string => {
  const values: { [key: string]: string } = {
    "box-20x20": "box",
    "box-28x28": "box",
  };
  return values[optionValueTag] || optionValueTag;
};

export const ignoredOptionTag = (optionTag: string): boolean => {
  const values: { [key: string]: string } = {
    polabox_box: "box",
  };
  return values[optionTag] !== undefined;
};

export const ignoredProductTag = (productTag: string): boolean => {
  const values: { [key: string]: string } = {
    gift: "gift",
  };
  return values[productTag] !== undefined;
};

export const getTotalPrice = (
  upsells: Upsell[],
  selectedUpsellsTags: Array<{ tag: string; value: string }>
): number => {
  const price = upsells
    .map((upsell) => {
      return selectedUpsellsTags.find(
        (tags) => tags.tag === upsell.tag && tags.value === upsell.value
      )
        ? upsell.price.cents
        : 0;
    })
    .reduce((accumulator, currentValue) => accumulator + currentValue);
  return price;
};

export const getFormattedTotalPrice = (
  upsells: Upsell[],
  selectedUpsellsTags: Array<{ tag: string; value: string }>,
  intl: IntlShape
): string => {
  const currency = upsells[0].price.currency;
  const price = getTotalPrice(upsells, selectedUpsellsTags);
  const formattedPrice = intl.formatNumber(price, {
    currency,
    style: "currency",
  });
  return formattedPrice;
};

export const isSelected = (
  tag: string,
  value: string,
  selectedUpsellsTags: UpsellIdentifiers[]
): boolean => {
  return selectedUpsellsTags.findIndex(
    (upsellTags) => upsellTags.tag === tag && upsellTags.value === value
  ) >= 0
    ? true
    : false;
};

export const formatSelectedOptions = (
  selectedUpsells: UpsellIdentifiers[],
  productOptions: CheerzApiCustomizationProductOption[] | undefined
): SelectedUpsell[] | undefined => {
  if (productOptions) {
    const productOptionsTags = productOptions.map((productOption) => productOption.tag);
    return selectedUpsells
      .filter((upsellProduct) => productOptionsTags.includes(upsellProduct.tag))
      .map((option) => {
        return {
          quantity: 1,
          tag: option.tag,
          value: option.value,
        };
      });
  }
};

export const convertFromApiToUpsells = (
  productOptions: CheerzApiCustomizationProductOption[],
  intl: IntlShape,
  currency: Currency
): Upsell[] => {
  const authorizedUpsellsTags = authorizedUpsells.map((upsell) => upsell.tag);

  const flattenedOptions = productOptions.reduce((reducedOptions, productOption) => {
    if (
      productOption.option_values.length === 0 ||
      (productOption.option_values.length === 1 &&
        authorizedUpsellsTags.includes(productOption.tag) === false)
    ) {
      return reducedOptions;
    }

    if (productOption.option_values.length > 1) {
      const includedOptions = productOption.option_values
        .filter((optionValue) => authorizedUpsellsTags.includes(optionValue.tag))
        .map((optionValue) => {
          return { ...productOption, option_values: [], value: optionValue };
        });
      return [...reducedOptions, ...includedOptions];
    } else {
      return [...reducedOptions, productOption];
    }
  }, [] as UpsellWhitelistedProductOption[]);

  return flattenedOptions.map((option) => {
    const optionRefTag = option.value ? option.value.tag : option.tag;
    const authorizedUpsell = authorizedUpsells.find((upsell) => upsell.tag === optionRefTag);
    const imageUrl = authorizedUpsell ? authorizedUpsell.imageUrl : "";

    return {
      description: intl.formatMessage({
        id: `cheerz_UpsellsModal_description__${optionRefTag}`,
      }),
      imageUrl,
      price: {
        cents: option.value ? option.value.price : option.option_values[0].price,
        currency,
      },
      tag: option.tag,
      title: intl.formatMessage({ id: `cheerz_UpsellsModal_title__${optionRefTag}` }),
      value: option.value ? option.value.tag : option.option_values[0].tag,
    };
  });
};
